import { Stack, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import moment from 'moment-timezone';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { StyledFormSection } from './_elements';
import RecurrenceRuleSection from './RecurrenceRuleSection';
import { getTsDueTimeMode } from '@src/utils/index_ts';
import { ServiceState } from '@src/store/reducers/serviceData';
import storeConnector from '@src/store/storeConnector';
import { SiteMeta } from '@src/types/SiteMeta';

interface FormValues {
  start: string;
  end: string;
}

interface DateSectionProps {
  siteMeta: SiteMeta;
  timeMode: ServiceState['timeMode'];
}

function DateSection({ siteMeta, timeMode }: DateSectionProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();
  const currentTZ =
    getTsDueTimeMode(siteMeta, timeMode, moment().toISOString()).tz() || 'UTC';
  return (
    <StyledFormSection>
      <Typography variant='h6' sx={{ mt: 2 }}>
        Date & Time
      </Typography>
      <Stack direction='row' spacing={2} sx={{ my: 2 }}>
        <Controller
          name='start'
          control={control}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                {...field}
                value={
                  field.value &&
                  moment(field.value, moment.ISO_8601, true).isValid()
                    ? moment.utc(field.value).tz(currentTZ)
                    : null
                }
                onChange={(date) => field.onChange(date?.toISOString() || '')}
                label='Start Date'
                slotProps={{
                  textField: {
                    error: !!errors.start,
                    helperText: errors.start?.message,
                  },
                  openPickerIcon: {
                    sx: { color: 'primary.main' },
                  },
                }}
                sx={{
                  flex: 1,
                }}
              />
            </LocalizationProvider>
          )}
        />
        <Controller
          name='end'
          control={control}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                {...field}
                value={
                  field.value &&
                  moment(field.value, moment.ISO_8601, true).isValid()
                    ? moment.utc(field.value).tz(currentTZ)
                    : null
                }
                onChange={(date) => field.onChange(date?.toISOString() || '')}
                label='End Date'
                slotProps={{
                  textField: {
                    error: !!errors.end,
                    helperText: errors.end?.message,
                  },
                  openPickerIcon: {
                    sx: { color: 'primary.main' },
                  },
                }}
                sx={{
                  flex: 1,
                }}
              />
            </LocalizationProvider>
          )}
        />
      </Stack>
      <RecurrenceRuleSection />
    </StyledFormSection>
  );
}

export default storeConnector(DateSection, {
  config: ['siteMeta'],
  service: ['timeMode'],
});
